<template>
  <div class="write-page">
    <!-- 헤더 -->
    <AppHeader :title="isEdit ? '글 수정' : '글쓰기'" backTo="/community" />

    <!-- 폼 -->
    <div class="write-form">
      <!-- 카테고리 선택 -->
      <div class="form-section">
        <label class="form-label">카테고리</label>
        <div class="category-chips">
          <button
            v-for="cat in writableCategories"
            :key="cat.id"
            :class="['category-chip', { active: form.category === cat.id }]"
            @click="form.category = cat.id"
          >
            {{ cat.label }}
          </button>
        </div>
      </div>

      <!-- 제목 -->
      <div class="form-section">
        <label class="form-label">제목</label>
        <input
          v-model="form.title"
          type="text"
          placeholder="제목을 입력하세요"
          class="form-input"
          maxlength="100"
        />
        <span class="char-count">{{ form.title.length }}/100</span>
      </div>

      <!-- 내용 -->
      <div class="form-section">
        <label class="form-label">내용</label>
        <textarea
          v-model="form.content"
          placeholder="내용을 입력하세요"
          class="form-textarea"
          rows="10"
        ></textarea>
      </div>

      <!-- 이미지 첨부 -->
      <div class="form-section">
        <label class="form-label">이미지 첨부</label>
        <p class="form-hint">최대 3장, 장당 5MB 이하 (자동 WEBP 압축)</p>

        <div class="image-upload-area">
          <!-- 이미지 미리보기 -->
          <div
            v-for="(img, index) in imagePreviewList"
            :key="index"
            class="image-preview-item"
          >
            <img :src="img.preview" alt="미리보기" />
            <button
              type="button"
              class="image-remove-btn"
              @click="removeImage(index)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <div v-if="img.compressing" class="image-compressing">
              <span>압축중...</span>
            </div>
          </div>

          <!-- 추가 버튼 -->
          <label
            v-if="imagePreviewList.length < 3"
            class="image-add-btn"
          >
            <input
              type="file"
              accept="image/*"
              multiple
              @change="handleImageSelect"
              class="hidden-input"
            />
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span>사진 추가</span>
          </label>
        </div>
      </div>

      <!-- 등록 버튼 -->
      <button
        @click="handleSubmit"
        :disabled="!canSubmit || loading || isCompressing"
        class="submit-btn"
      >
        {{ loading ? '저장중...' : (isCompressing ? '이미지 압축중...' : (isEdit ? '수정하기' : '등록하기')) }}
      </button>
    </div>

    <!-- 안내 문구 -->
    <div class="notice-box">
      <p>커뮤니티 이용 규칙을 지켜주세요.</p>
      <ul>
        <li>타인을 비방하거나 욕설을 포함한 글은 삭제될 수 있습니다.</li>
        <li>광고성 글이나 도배 글은 경고 없이 삭제됩니다.</li>
        <li>개인정보(전화번호, 주소 등)를 포함하지 마세요.</li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import AppHeader from '@/components/common/AppHeader.vue'
import { useCommunity, COMMUNITY_CATEGORIES } from '@/composables/main/useCommunity'
import { useAuth } from '@/composables/common/useAuth'
import { storage } from '@/firebase/firebase'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'

const router = useRouter()
const route = useRoute()
const { createPost, updatePost, getPost, loading } = useCommunity()
const { user, isLoggedIn } = useAuth()

// all 제외한 카테고리
const writableCategories = COMMUNITY_CATEGORIES.filter(c => c.id !== 'all')

const form = ref({
  title: '',
  content: '',
  category: 'free'
})

// 이미지 관련
const imagePreviewList = ref([]) // { file, preview, compressing, compressed }
const isCompressing = computed(() => imagePreviewList.value.some(img => img.compressing))

const isEdit = computed(() => !!route.query.edit)
const editId = computed(() => route.query.edit)

const canSubmit = computed(() => {
  return form.value.title.trim() && form.value.content.trim() && form.value.category
})

// 이미지를 WEBP로 압축하는 함수
const compressToWebp = (file, maxWidth = 1200, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let { width, height } = img

        // 최대 너비 제한
        if (width > maxWidth) {
          height = (height * maxWidth) / width
          width = maxWidth
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), {
                type: 'image/webp'
              }))
            } else {
              reject(new Error('WEBP 변환 실패'))
            }
          },
          'image/webp',
          quality
        )
      }
      img.onerror = () => reject(new Error('이미지 로드 실패'))
      img.src = e.target.result
    }
    reader.onerror = () => reject(new Error('파일 읽기 실패'))
    reader.readAsDataURL(file)
  })
}

// 이미지 선택 핸들러
const handleImageSelect = async (e) => {
  const files = Array.from(e.target.files)
  if (!files.length) return

  // 최대 3장 제한
  const remainingSlots = 3 - imagePreviewList.value.length
  const filesToAdd = files.slice(0, remainingSlots)

  for (const file of filesToAdd) {
    // 파일 크기 체크 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(`${file.name}의 크기가 5MB를 초과합니다.`)
      continue
    }

    // 이미지 타입 체크
    if (!file.type.startsWith('image/')) {
      alert(`${file.name}은(는) 이미지 파일이 아닙니다.`)
      continue
    }

    // 미리보기 URL 생성
    const preview = URL.createObjectURL(file)
    const index = imagePreviewList.value.length

    imagePreviewList.value.push({
      file: null,
      preview,
      compressing: true,
      compressed: false
    })

    // WEBP 압축
    try {
      const compressedFile = await compressToWebp(file)
      imagePreviewList.value[index].file = compressedFile
      imagePreviewList.value[index].compressing = false
      imagePreviewList.value[index].compressed = true
    } catch (err) {
      console.error('이미지 압축 실패:', err)
      // 압축 실패 시 원본 사용
      imagePreviewList.value[index].file = file
      imagePreviewList.value[index].compressing = false
      imagePreviewList.value[index].compressed = false
    }
  }

  // input 초기화
  e.target.value = ''
}

// 이미지 제거
const removeImage = (index) => {
  // 메모리 해제
  URL.revokeObjectURL(imagePreviewList.value[index].preview)
  imagePreviewList.value.splice(index, 1)
}

// 이미지 업로드 (Firebase Storage)
const uploadImages = async () => {
  const imageUrls = []

  for (const img of imagePreviewList.value) {
    if (!img.file) continue

    const fileName = `community/${Date.now()}_${Math.random().toString(36).substr(2, 9)}.webp`
    const fileRef = storageRef(storage, fileName)

    await uploadBytes(fileRef, img.file)
    const url = await getDownloadURL(fileRef)
    imageUrls.push(url)
  }

  return imageUrls
}

// 수정 모드일 때 기존 데이터 로드
const loadExistingPost = async () => {
  if (!editId.value) return

  const post = await getPost(editId.value)
  if (post) {
    // 본인 글인지 확인
    if (post.userId !== user.value?.uid) {
      alert('수정 권한이 없습니다.')
      router.replace('/community')
      return
    }

    form.value.title = post.title
    form.value.content = post.content
    form.value.category = post.category

    // 기존 이미지 로드
    if (post.images && post.images.length > 0) {
      for (const url of post.images) {
        imagePreviewList.value.push({
          file: null, // 기존 이미지는 file 없음
          preview: url,
          compressing: false,
          compressed: true,
          existingUrl: url // 기존 URL 표시
        })
      }
    }
  } else {
    alert('게시글을 찾을 수 없습니다.')
    router.replace('/community')
  }
}

// 제출
const handleSubmit = async () => {
  if (!canSubmit.value || loading.value || isCompressing.value) return

  if (!isLoggedIn.value) {
    alert('로그인이 필요합니다.')
    router.push('/login')
    return
  }

  try {
    // 이미지 업로드
    const existingUrls = imagePreviewList.value
      .filter(img => img.existingUrl)
      .map(img => img.existingUrl)

    const newUrls = await uploadImages()
    const allImageUrls = [...existingUrls, ...newUrls]

    if (isEdit.value) {
      // 수정
      const result = await updatePost(editId.value, {
        title: form.value.title.trim(),
        content: form.value.content.trim(),
        category: form.value.category,
        images: allImageUrls
      })

      if (result.success) {
        router.replace(`/community/${editId.value}`)
      } else {
        alert('수정에 실패했습니다.')
      }
    } else {
      // 새 글 작성
      const result = await createPost({
        title: form.value.title.trim(),
        content: form.value.content.trim(),
        category: form.value.category,
        userId: user.value.uid,
        userEmail: user.value.email,
        userName: user.value.displayName || '',
        images: allImageUrls
      })

      if (result.success) {
        router.replace(`/community/${result.id}`)
      } else {
        alert('등록에 실패했습니다.')
      }
    }
  } catch (err) {
    console.error('저장 실패:', err)
    alert('저장에 실패했습니다. 다시 시도해주세요.')
  }
}

onMounted(() => {
  if (!isLoggedIn.value) {
    alert('로그인이 필요합니다.')
    router.replace('/login')
    return
  }

  if (isEdit.value) {
    loadExistingPost()
  }
})
</script>

<style scoped>
.write-page {
  min-height: 100vh;
  background: #ffffff;
  max-width: 480px;
  margin: 0 auto;
  padding-bottom: 80px;
}

/* 폼 */
.write-form {
  background: white;
  padding: 20px 16px;
}

.form-section {
  margin-bottom: 24px;
  position: relative;
}

.form-section:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 10px;
}

.form-hint {
  font-size: 12px;
  color: #94a3b8;
  margin: -6px 0 10px 0;
}

/* 카테고리 칩 */
.category-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.category-chip {
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  background: #f1f5f9;
  border: 1px solid transparent;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.category-chip.active {
  color: #2563eb;
  background: #dbeafe;
  border-color: #2563eb;
}

/* 입력 필드 */
.form-input {
  width: 100%;
  padding: 14px 16px;
  font-size: 15px;
  color: #1e293b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  outline: none;
  transition: all 0.2s;
}

.form-input:focus {
  background: white;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-input::placeholder {
  color: #94a3b8;
}

.char-count {
  position: absolute;
  right: 12px;
  bottom: -20px;
  font-size: 12px;
  color: #94a3b8;
}

/* 텍스트에어리어 */
.form-textarea {
  width: 100%;
  padding: 14px 16px;
  font-size: 15px;
  color: #1e293b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  outline: none;
  resize: none;
  line-height: 1.6;
  transition: all 0.2s;
}

.form-textarea:focus {
  background: white;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-textarea::placeholder {
  color: #94a3b8;
}

/* 이미지 업로드 */
.image-upload-area {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.image-preview-item {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 12px;
  overflow: hidden;
  background: #f1f5f9;
}

.image-preview-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
}

.image-remove-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

.image-compressing {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 12px;
}

.image-add-btn {
  width: 100px;
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #f8fafc;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  color: #94a3b8;
}

.image-add-btn:hover {
  border-color: #2563eb;
  color: #2563eb;
  background: #eff6ff;
}

.image-add-btn span {
  font-size: 12px;
  font-weight: 500;
}

.hidden-input {
  display: none;
}

/* 등록 버튼 */
.submit-btn {
  width: 100%;
  padding: 16px;
  margin-top: 24px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: #2563eb;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s;
}

.submit-btn:hover:not(:disabled) {
  background: #1d4ed8;
}

.submit-btn:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

/* 안내 문구 */
.notice-box {
  margin: 16px;
  padding: 16px;
  background: #fef3c7;
  border-radius: 12px;
}

.notice-box p {
  font-size: 13px;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 8px 0;
}

.notice-box ul {
  margin: 0;
  padding-left: 16px;
}

.notice-box li {
  font-size: 12px;
  color: #a16207;
  line-height: 1.6;
}
</style>
